/**
 * Pure helper + constant for the `useInterviewState` belt-and-braces
 * poll (#553). Lives in `shared/` so both the renderer's
 * `useInterviewStatePoll` hook AND the `Workspace.tsx` integration
 * tests can import without dragging the full Workspace shell into
 * the unit test (which would need react-query, react-router, IPC
 * stubs, the chat reducer, ...).
 *
 * See `renderer/hooks/useInterviewStatePoll.ts` for the full
 * design prose explaining why the poll exists at all, what failure
 * modes it catches, and why the broadened gate replaced the pre-#553
 * narrow (depth + subPhase) gate.
 */
/**
 * Decide whether the renderer should run a 3 s safety-net poll on
 * the `build-game:get-interview-state` query. Pure — no side
 * effects, no React deps. Returns true when the interview is
 * non-terminal (or absent — file may not exist yet).
 *
 * The shape is intentionally simple so the regression suite can
 * exhaustively pin every branch (terminal vs non-terminal vs
 * unknown vs missing) without setting up the full Workspace.
 */
export function shouldPollInterviewState(args) {
    const s = args.status;
    if (s === undefined) {
        // No state on disk yet. The most common path: the user opened the
        // Workspace before typing `/build-game`, so we expect the SKILL
        // / engine to drop a fresh state file moments later. Keep polling
        // so the renderer wakes up when the file appears.
        return true;
    }
    // Terminal statuses don't mutate without user action.
    if (s === 'built' || s === 'built-partial' || s === 'abandoned') {
        return false;
    }
    // Every other status (including unknown future ones) might still
    // be flipped by the SKILL — keep the safety-net poll on. Catches
    // 'in-progress', 'complete', 'dispatched', 'building', and any
    // forward-compat persisted value the GUI doesn't yet enumerate.
    return true;
}
/**
 * Polling cadence in milliseconds. 3 s matches the pre-#553 narrow
 * poll's cadence — broadening the gate without slowing the tick rate
 * keeps plan-mode snappy without burning IPC on terminal states.
 * Exported so the unit suite can override it for fast tests.
 */
export const INTERVIEW_STATE_LIVE_POLL_MS = 3_000;
//# sourceMappingURL=interview-poll-gate.js.map